import {trackActiveAgentModal} from './modalLifecycle';
import {FIELD_CONTRACT_KEY} from './fieldContract';
import {observeModal} from './modalEvidence';

/**
 * Merge verified reference IDs into canonical modal initial values.
 * @param {object} params semantic modal parameters
 * @return {object} agent initial values
 */
export function buildModalInitialValues(params = {}) {
	const values = {...(params.initial_values || {})};
	Object.entries(params.choice_values || {}).forEach(([fieldId, value]) => {
		if (!/^[a-z][a-z0-9_]{0,63}$/.test(fieldId)) return;
		if (value == null || !String(value).trim()) return;
		values[`${fieldId}_frontend_value`] = String(value).trim();
	});
	Object.entries(params.resolved_references || {}).forEach(([fieldId, entity]) => {
		if (!/^[a-z][a-z0-9_]{0,63}$/.test(fieldId)) return;
		if (!entity || typeof entity !== 'object') return;
		if (entity.entity_id != null && String(entity.entity_id).trim()) {
			values[`${fieldId}_id`] = String(entity.entity_id).trim();
		}
		if (
			values[fieldId] == null &&
			typeof entity.name === 'string' &&
			entity.name.trim()
		) {
			values[fieldId] = entity.name.trim();
		}
		Object.entries(entity.metadata || {}).forEach(([metadataId, value]) => {
			if (!/^[a-z][a-z0-9_]{0,63}$/.test(metadataId)) return;
			if (value == null || (typeof value === 'string' && !value.trim())) return;
			values[`${fieldId}_${metadataId}`] = value;
		});
	});
	if (
		params.field_contract &&
		typeof params.field_contract === 'object'
	) {
		values[FIELD_CONTRACT_KEY] = params.field_contract;
	}
	return values;
}

// Long enough that a listener already mounted has answered - they do so on
// the next animation frame - and short enough that one arriving with its
// section is not kept waiting.
const RETRY_INTERVAL_MS = 150;

// Forms can fetch their record after the destination is ready. The live AP
// configure check exceeded the previous 5s budget before its identity became
// observable. Leave 2.5s for section focus inside the dispatcher's 15s budget.
// This is a maximum: observed forms still resolve on the next check.
const ACKNOWLEDGEMENT_TIMEOUT_MS = 12000;

/**
 * Open a resource-owned Canvas modal.
 * @param {string} eventName resource event identifier
 * @param {object} command semantic modal command
 * @param {object} envelope request correlation
 * @param {number} timeoutMs acknowledgement timeout
 * @param {number} retryMs how often to send again while nobody has answered
 * @return {Promise<object>} what the page reported about the form it opened
 */
export function openResourceModal(
	eventName,
	command,
	envelope,
	timeoutMs = ACKNOWLEDGEMENT_TIMEOUT_MS,
	retryMs = RETRY_INTERVAL_MS,
) {
	// Carried on the envelope rather than as an argument. The envelope
	// already passes untouched through every resource's modal handler, so
	// cancellation reaches all of them without each one being rewritten to
	// forward a parameter it otherwise has no interest in.
	const signal = envelope?.signal;
	return new Promise((resolve, reject) => {
		// A browser event is heard only by whoever is listening at the moment
		// it is sent - there is no queue holding it for a listener that
		// arrives later. The table a modal belongs to mounts when its section
		// opens, and the section reports itself open before React has
		// finished mounting it, so a single send arrived at nobody and the
		// wait that followed had nothing left to answer it. Sent again until
		// someone does, which is how a control inside a freshly opened
		// section is already found.
		let settled = false;
		let claimed = false;
		let reported;
		let retry = null;
		let observation = null;
		const onAbort = () => {
			if (settled) return;
			stop();
			reject(new Error('command_cancelled'));
		};
		const stop = () => {
			settled = true;
			clearTimeout(timer);
			if (retry !== null) clearInterval(retry);
			if (observation !== null) clearInterval(observation);
			signal?.removeEventListener('abort', onAbort);
		};
		const timer = setTimeout(() => {
			if (settled) return;
			stop();
			// A listener accepted the command but the form never became
			// observable. Keep that distinct from nobody accepting it.
			if (claimed) resolve(observeModal(reported));
			else reject(new Error('resource_modal_timeout'));
		}, timeoutMs);
		// L07. The turn can give up while this is still retrying, and a
		// retry loop that outlives its turn opens a modal seconds after the
		// user moved on - on top of whatever they are doing now. The abort
		// stops the sending, which is the part that can still cause an
		// effect; anything already opened is Canvas's and is left alone.
		if (signal) {
			if (signal.aborted) {
				stop();
				reject(new Error('command_cancelled'));
				return;
			}
			signal.addEventListener('abort', onAbort, {once: true});
		}
		const send = () => {
			if (settled || claimed) return;
			window.dispatchEvent(new CustomEvent(eventName, {
				detail: {
					context: command.params?.context,
					entityId: command.params?.entity_id,
					// Plural counterpart for commands that target more than one
					// row at once (e.g. deleting several resolved users in one
					// go). Undefined whenever a command carries only the
					// singular entity_id, so existing single-entity listeners
					// are unaffected.
					entityIds: Array.isArray(command.params?.entity_ids)
						? command.params.entity_ids
						: undefined,
					params: command.params || {},
					initialValues: buildModalInitialValues(command.params),
					// Whoever answers first owns the modal. A second listener
					// answering a later send is ignored rather than opening a
					// second one.
					//
					// `seen` is what the page can say about the form it just
					// rendered - which modal, which record, which variant.
					// Optional: a listener that passes nothing resolves with
					// nothing, exactly as before, and the backend calls that
					// unconfirmed rather than pretending otherwise.
					//
					// A listener that does pass it has usually already done
					// the work - these handlers check the command's record
					// against the one on screen before opening anything, and
					// that check was being made and then thrown away.
					open: (close, seen) => {
						if (settled || claimed) return;
						claimed = true;
						reported = seen;
						if (retry !== null) clearInterval(retry);
						trackActiveAgentModal({
							request_id: envelope.request_id,
							command_id: command.command_id,
							session_id: envelope.session_id,
							workflow_instance_id: command.workflow_instance_id,
							workflow_revision: command.workflow_revision,
							idempotency_key: command.idempotency_key,
						}, close);
						// Resolved with nothing when the page says nothing, as
						// it always was: the dispatcher turns that into an
						// empty result anyway, and changing it here would
						// only rewrite what every listener that reports
						// nothing already means.
						if (!seen || typeof seen !== 'object') {
							stop();
							resolve(undefined);
							return;
						}
						const check = () => {
							if (settled) return;
							const evidence = observeModal(seen);
							if (!evidence?.observed) return;
							stop();
							resolve(evidence);
						};
						check();
						if (!settled) observation = setInterval(check, 50);
					},
					fail: (reason) => {
						if (settled || claimed) return;
						stop();
						reject(new Error(reason || 'resource_modal_failed'));
					},
				},
			}));
		};
		// The form standing open is an answer, even if nobody said so.
		//
		// A listener claims the command from inside `requestAnimationFrame`,
		// and a browser suspends that in a hidden tab - so a user who switches
		// away while the form opens gets `resource_modal_timeout` for a form
		// that is plainly on screen. Measured: `document.hidden` true,
		// `requestAnimationFrame` never firing, the modal rendered the whole
		// time. The section path already resolves on what the page shows; this
		// is the same rule for forms.
		//
		// Matched against the id this command asked for, which is what
		// `observeModal` compares with `data-agent-modal-id`, so a form that
		// is not the one asked for still answers nothing.
		const asked = command?.modal_id ? {modal_id: command.modal_id} : null;
		const watchForItself = () => {
			if (settled || claimed || !asked) return;
			const evidence = observeModal(asked);
			if (!evidence?.observed) return;
			stop();
			resolve(evidence);
		};
		send();
		watchForItself();
		if (!settled && !claimed && retryMs > 0) {
			retry = setInterval(() => {
				watchForItself();
				if (!settled && !claimed) send();
			}, retryMs);
		}
	});
}
